import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { User, Smartphone, Lock, Gift, Eye, EyeOff, ChevronRight, ShieldCheck, Zap, Trophy, Headphones } from "lucide-react";

export default function AuthPage({ initialMode = "register" }) {
  const { login, register, user, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const [mode, setMode] = useState(initialMode === "login" ? "login" : "register");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && !user._stale) navigate("/", { replace: true }); }, [user, navigate]);
  useEffect(() => {
    const ref = (search.get("ref") || "").trim().toUpperCase();
    if (ref) { setRefCode(ref); setMode("register"); }
  }, [search]);

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) return toast.error("10-digit mobile number daalein");
    if (password.length < 4) return toast.error(isLogin ? "Password daalein" : "Password kam se kam 4 characters");
    if (!isLogin && name.trim().length < 2) return toast.error("Apna naam daalein");
    setLoading(true);
    try {
      if (isLogin) {
        await login(mobile, password);
      } else {
        await register(mobile, name, password, refCode || undefined);
        toast.success("Welcome to J&K CLUB!");
      }
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden flex flex-col items-center px-5 pt-9 pb-8"
         data-testid={isLogin ? "login-page" : "register-page"}
         style={{ background: "radial-gradient(130% 80% at 50% -10%, #14321f 0%, #0a1710 38%, #050806 100%)" }}>
      <Toaster richColors position="top-center" />

      {/* decorative glow + felt + suit watermarks */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-40 blur-3xl"
           style={{ background: "radial-gradient(circle, rgba(212,175,55,0.45), transparent 62%)" }} />
      <div className="pointer-events-none absolute inset-0 pattern-grid opacity-[0.05]" />
      <div className="pointer-events-none absolute top-24 left-6 text-6xl text-yellow-500/[0.06] font-serif select-none">♠</div>
      <div className="pointer-events-none absolute top-40 right-7 text-5xl text-red-500/[0.07] font-serif select-none">♥</div>
      <div className="pointer-events-none absolute bottom-28 left-8 text-5xl text-red-500/[0.06] font-serif select-none">♦</div>
      <div className="pointer-events-none absolute bottom-16 right-9 text-6xl text-yellow-500/[0.06] font-serif select-none">♣</div>

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
        {/* Logo + brand name */}
        <div className="flex flex-col items-center animate-logo-pop">
          <div className="w-20 h-20 rounded-full p-[3px] bg-gold-gradient shadow-[0_8px_30px_rgba(212,175,55,0.5)]">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
              <img src="/brand/jk-club-logo.png?v=4" alt="J&K CLUB" className="w-full h-full object-cover" data-testid="auth-logo" />
            </div>
          </div>
          <h1 className="mt-3 text-3xl font-display font-black text-gold-shine tracking-tight leading-none" data-testid="auth-brand">J&amp;K CLUB</h1>
          <div className="mt-1.5 flex items-center gap-2 text-[10px] font-black tracking-[0.3em] text-yellow-300/70">
            <span className="text-yellow-500">♠</span> PLAY · WIN · REPEAT <span className="text-yellow-500">♠</span>
          </div>
        </div>

        {/* Tabs: Create Account (default) + Login */}
        <div className="mt-6 w-full grid grid-cols-2 p-1 rounded-2xl bg-black/50 border border-yellow-500/25 relative" data-testid="auth-tabs">
          <span
            className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-xl bg-gold-gradient transition-transform duration-300 ease-out shadow-[0_6px_18px_rgba(212,175,55,0.4)]"
            style={{ transform: isLogin ? "translateX(calc(100% + 4px))" : "translateX(0)" }}
          />
          <button
            type="button"
            onClick={() => setMode("register")}
            data-testid="auth-tab-register"
            className={`relative z-10 h-11 rounded-xl text-[13px] font-black tracking-wide transition-colors ${!isLogin ? "text-black" : "text-white/70"}`}
          >
            CREATE ACCOUNT
          </button>
          <button
            type="button"
            onClick={() => setMode("login")}
            data-testid="auth-tab-login"
            className={`relative z-10 h-11 rounded-xl text-[13px] font-black tracking-wide transition-colors ${isLogin ? "text-black" : "text-white/70"}`}
          >
            LOGIN
          </button>
        </div>

        {/* Form card */}
        <form onSubmit={submit} autoComplete="off" data-testid={isLogin ? "login-form" : "register-form"}
              className="mt-4 w-full rounded-3xl p-4 border border-yellow-500/35 backdrop-blur-md animate-slide-up"
              style={{ background: "linear-gradient(180deg, rgba(10,16,12,0.92) 0%, rgba(0,0,0,0.95) 100%)" }}>

          {!isLogin && (
            <Field icon={<User size={18} />}>
              <input className="auth-field-input" placeholder="Enter Full Name" value={name}
                     onChange={(e) => setName(e.target.value)} autoComplete="off" data-testid="register-name-input" />
            </Field>
          )}

          <Field icon={<Smartphone size={18} />}>
            <input className="auth-field-input" type="tel" inputMode="numeric" maxLength={10}
                   placeholder="Enter Mobile Number" value={mobile}
                   onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                   autoComplete="off" data-testid={isLogin ? "login-mobile-input" : "register-mobile-input"} />
          </Field>

          <Field icon={<Lock size={18} />}>
            <input className="auth-field-input pr-10" type={showPw ? "text" : "password"}
                   placeholder={isLogin ? "Enter Password" : "Create Password"} value={password}
                   onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                   data-testid={isLogin ? "login-mpin-input" : "register-mpin-input"} />
            <button type="button" onClick={() => setShowPw((s) => !s)} tabIndex={-1} data-testid="toggle-pw"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-400/70">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </Field>

          {!isLogin && (
            <Field icon={<Gift size={18} />}>
              <input className="auth-field-input" placeholder="Referral Code (Optional)" value={refCode}
                     onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12))}
                     autoComplete="off" data-testid="register-refcode-input" />
            </Field>
          )}

          {isLogin && (
            <div className="text-right -mt-1 mb-1">
              <Link to="/forgot-password" data-testid="link-forgot" className="text-[12px] font-bold text-yellow-300/90 hover:text-yellow-200">
                Forgot Password?
              </Link>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"}
                  className="mt-2 w-full h-12 rounded-xl bg-gold-gradient text-black font-black tracking-widest shadow-[0_10px_28px_rgba(212,175,55,0.45)] disabled:opacity-50 active:scale-[0.98] transition btn-shine flex items-center justify-center gap-1">
            {loading ? "…" : (isLogin ? "LOGIN" : "SIGN UP")}
            <ChevronRight size={20} strokeWidth={3} />
          </button>

          <div className="mt-3 text-center text-[12px] text-white/55">
            {isLogin ? (
              <>Naya account?{" "}
                <button type="button" onClick={() => setMode("register")} data-testid="link-to-register" className="font-black text-yellow-300">Create Account</button>
              </>
            ) : (
              <>Already a member?{" "}
                <button type="button" onClick={() => setMode("login")} data-testid="link-to-login" className="font-black text-yellow-300">Login</button>
              </>
            )}
          </div>
        </form>

        {/* features */}
        <div className="mt-6 w-full grid grid-cols-4 gap-2" data-testid="features-row">
          <Feature icon={<ShieldCheck size={18} />} label={"100%\nSecure"} />
          <Feature icon={<Zap size={18} />} label={"Fast\nPayouts"} />
          <Feature icon={<Trophy size={18} />} label={"Instant\nResults"} />
          <Feature icon={<Headphones size={18} />} label={"24×7\nSupport"} />
        </div>
      </div>

      <style>{`
        .auth-field-input { width:100%; height:48px; background:transparent; color:#fef9e7; outline:none; font-size:15px; padding-left:44px; padding-right:12px; }
        .auth-field-input::placeholder { color: rgba(255,255,255,0.32); }
      `}</style>
    </div>
  );
}

const Field = ({ icon, children }) => (
  <div className="relative mb-3 rounded-2xl border border-yellow-500/25 bg-black/60 focus-within:border-yellow-400/70 focus-within:shadow-[0_0_18px_rgba(212,175,55,0.18)] transition">
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-400">{icon}</span>
    {children}
  </div>
);

const Feature = ({ icon, label }) => (
  <div className="flex flex-col items-center text-center gap-1.5">
    <div className="w-11 h-11 rounded-xl bg-black/50 border border-yellow-500/25 flex items-center justify-center text-yellow-400">{icon}</div>
    <span className="text-[9px] font-bold text-white/55 leading-tight whitespace-pre-line">{label}</span>
  </div>
);
